import {
  Box,
  Text,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import DayPicker from 'modules/core/components/DayPicker'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/shift_management/components/ShiftManagement/CopyPeriodWizard/utils'
import { CopyTypeEnum } from 'modules/shift_management/components/ShiftManagement/CopyPeriodWizard/utils'
import { maxDate, minDate } from 'modules/shift_management/lib/getMinMaxDates'

type DateValues = string | undefined

const DestinationStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const {
    daysSchedule,
    endDateSchedule,
    daysCoverage,
    endDateCoverage,
    destinationStartDate,
    copyType,
  } = data
  const days = copyType === CopyTypeEnum.schedule ? daysSchedule : daysCoverage
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()

  useEffect(() => {
    const endDate = copyType === CopyTypeEnum.schedule ? endDateSchedule : endDateCoverage
    const destinationDate = dateToString(stringToDate(endDate, 'backend').add(1, 'days'), 'backend')
    const value = !!destinationStartDate ? destinationStartDate : destinationDate
    onDataChanged({ ...data, destinationStartDate: value })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // This component is not ready to be rendered without a destinationStartDate from the useEffect
  if (!destinationStartDate) return null

  const onChangeDestination = (values: DateValues) => {
    onDataChanged({ ...data, destinationStartDate: values || '' })
  }

  return (
    <Box
      border={{
        all: {
          width: 's2',
          style: 'solid',
          color: 'grey400',
        },
      }}
      borderRadius={{ all: 'abs008' }}
      background="grey000White"
      width="s600"
    >
      <Box
        alignItems="center"
        borderRadius={{ all: 'abs008' }}
        padding="s16"
        flexDirection="row"
        justifyContent="spaceBetween"
        background="grey000White"
      >
        <Box flexDirection="column" justifyContent="spaceBetween" gap="s12">
          <Text.Normal size="200" weight="bold" color="grey1000">
            {i18n.t('shift_management.copy_schedule_period.destination_step.title')}
          </Text.Normal>

          <DayPicker
            mode="single"
            onChange={onChangeDestination}
            value={destinationStartDate}
            valueFormat="backend"
            firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
            minDate={minDate}
            maxDate={maxDate}
          />

          <Box background="grey300" borderRadius={{ all: 'abs008' }} padding="s24">
            <Text.Normal color="grey1000">
              {i18n.t('shift_management.copy_schedule_period.destination_step.message', { days })}
            </Text.Normal>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const destinationStep: () => WizardStep<WizardData> = () => ({
  title: i18n.t('shift_management.copy_schedule_period.destination_step.title_step'),
  nextText: i18n.t('shift_management.copy_schedule_period.destination_step.next_text'),

  render: (props) => <DestinationStep {...props} />,
  isCompleted: (data) => !!data.destinationStartDate,
})
